import { useEffect, useMemo, useRef, useState, type MouseEvent as RMouse } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useColumnDistinct } from '../modules/queries';
import type { ModuleResponse } from '../modules/types';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useFetchMs } from '../../hooks/useFetchMs';
import { fmtLocalDate } from '../../utils/datetime';
import './trend.css';

const KEY = 'trendanalysis';
const CHART_COLOR = '#FB6E2B';   /* token-exempt: chart series colour */

type Row = Record<string, unknown>;
type ChartType = 'line' | 'bar' | 'area';
interface Pt { date: number; value: number; raw: string; job: string; sample: string; site: string; client: string; pid: string; pname: string; units: string; rl: string; dateStr: string; }

const str = (v: unknown): string => (v == null ? '' : String(v));
// Shared §14b.6 helper (MM/DD/YYYY, no inline date code); raw fallback when
// the value isn't parseable.
const fmtDate = (v: unknown): string => {
  const s = str(v);
  if (!s) return '';
  return fmtLocalDate(s) || s;
};
const toNum = (v: unknown): number => {
  const n = parseFloat(str(v).replace(/[^0-9eE.+-]/g, ''));
  return Number.isFinite(n) ? n : NaN;
};
const niceMax = (vMax: number): number =>
  vMax <= 0 ? 1 : vMax < 1 ? 1 : vMax < 5 ? 5 : vMax < 25 ? 25 : vMax < 100 ? 100 : Math.ceil(vMax / 100) * 100;

/* ── Multi-select checked combo (v5 .tr-combo) ── */
function CheckedCombo({
  label, required, placeholder, column, selected, onChange, cascade,
}: {
  label: string; required?: boolean; placeholder: string; column: string;
  selected: string[]; onChange: (v: string[]) => void;
  cascade: Record<string, string[]>;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  // Options cascade on the OTHER combos' current selections.
  const { data: options = [], isLoading } = useColumnDistinct(KEY, column, '', null, true, cascade);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const shown = useMemo(
    () => options.filter((o) => o.toLowerCase().includes(q.trim().toLowerCase())),
    [options, q],
  );
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const display = selected.length === 0 ? placeholder
    : selected.length === 1 ? selected[0]! : `${selected.length} selected`;

  return (
    <div className="tr-field">
      <div className="tr-field-lbl">{label}{required && <span className="req" title="Required">*</span>}</div>
      <div className={`tr-combo${open ? ' open' : ''}${selected.length ? ' has-value' : ''}`} ref={ref}>
        <div className="tr-combo-display" onClick={() => setOpen((o) => !o)}>
          <span className={`tr-combo-value${selected.length ? '' : ' placeholder'}`}>{display}</span>
          {selected.length > 0 && (
            <span className="tr-combo-clear" onClick={(e) => { e.stopPropagation(); onChange([]); }}>&times;</span>
          )}
          <i className="ti ti-chevron-down chev" />
        </div>
        {open && (
          <div className="tr-combo-menu">
            <div className="tr-combo-search">
              <input autoFocus placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="tr-combo-actions">
              <a onClick={() => onChange(shown)}>Select all</a>
              <a onClick={() => onChange([])}>Clear</a>
              <span className="tr-combo-count">{selected.length} / {options.length}</span>
            </div>
            <div className="tr-combo-options">
              {isLoading && <div className="tr-combo-empty">Loading…</div>}
              {!isLoading && shown.length === 0 && <div className="tr-combo-empty">No matches</div>}
              {shown.map((o) => (
                <label key={o}>
                  <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
                  <span>{o || '(blank)'}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function TrendAnalysisPage({ onHome }: { onHome: () => void }) {
  const [client, setClient] = useState<string[]>([]);
  const [projectId, setProjectId] = useState<string[]>([]);
  const [projectName, setProjectName] = useState<string[]>([]);
  const [siteName, setSiteName] = useState<string[]>([]);
  const [parameter, setParameter] = useState<string[]>([]);
  // Live selections drive each combo's cascade (useColumnDistinct drops the
  // combo's own column so its list isn't restricted by itself).
  const cascade = { client, projectId, projectName, siteName, parameter };
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [chartType, setChartType] = useState<ChartType>('line');
  const [committed, setCommitted] = useState<null | {
    client: string[]; projectId: string[]; projectName: string[];
    siteName: string[]; parameter: string[]; from: string; to: string;
  }>(null);

  const [gSearch, setGSearch] = useState('');
  const [expOpen, setExpOpen] = useState(false);
  const expRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!expOpen) return;
    const h = (e: MouseEvent) => { if (expRef.current && !expRef.current.contains(e.target as Node)) setExpOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [expOpen]);

  const dataQ = useQuery({
    queryKey: ['trend-data', committed],
    enabled: committed != null,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async () => {
      const f: Record<string, string[]> = {};
      if (committed!.client.length) f.client = committed!.client;
      if (committed!.projectId.length) f.projectId = committed!.projectId;
      if (committed!.projectName.length) f.projectName = committed!.projectName;
      if (committed!.siteName.length) f.siteName = committed!.siteName;
      if (committed!.parameter.length) f.parameter = committed!.parameter;
      const params: Record<string, string | number> = { page: 1, pageSize: 10000 };
      if (Object.keys(f).length) params.filters = JSON.stringify(f);
      const { data } = await api.get<ModuleResponse>(`/api/v1/modules/${KEY}`, { params });
      return data;
    },
  });
  // Top load-time chip (shared) — times each fetch via the query's isFetching.
  const loadMs = useFetchMs(dataQ.isFetching);

  // Rows after client-side date filtering
  const rows = useMemo(() => {
    const all = (dataQ.data?.data ?? []) as Row[];
    if (!committed) return [];
    const from = committed.from ? new Date(committed.from).getTime() : null;
    const to = committed.to ? new Date(committed.to).getTime() + 86_400_000 : null;
    return all.filter((r) => {
      const t = new Date(str(r.dateAnalyzed)).getTime();
      if (Number.isNaN(t)) return false;
      if (from != null && t < from) return false;
      if (to != null && t >= to) return false;
      return true;
    });
  }, [dataQ.data, committed]);

  // Active parameter series (chart shows one parameter at a time, like v5)
  const activeParam = useMemo(() => {
    if (committed?.parameter.length) return committed.parameter[0]!;
    const freq = new Map<string, number>();
    for (const r of rows) { const p = str(r.parameter); if (p) freq.set(p, (freq.get(p) ?? 0) + 1); }
    let best = ''; let n = -1;
    for (const [p, c] of freq) if (c > n) { best = p; n = c; }
    return best;
  }, [committed, rows]);

  const points = useMemo<Pt[]>(() => {
    const pts: Pt[] = [];
    for (const r of rows) {
      if (activeParam && str(r.parameter) !== activeParam) continue;
      const v = toNum(r.result);
      const t = new Date(str(r.dateAnalyzed)).getTime();
      if (Number.isNaN(v) || Number.isNaN(t)) continue;
      pts.push({
        date: t, value: v, raw: str(r.result), job: str(r.jobId), sample: str(r.sampleId),
        site: str(r.siteName), client: str(r.client), pid: str(r.projectId),
        pname: str(r.projectName), units: str(r.units), rl: str(r.rl), dateStr: fmtDate(r.dateAnalyzed),
      });
    }
    return pts.sort((a, b) => a.date - b.date);
  }, [rows, activeParam]);

  const units = points[0]?.units ?? '';

  // ── Chart geometry (port of v5 trendDrawChart, dynamic time axis) ──
  const chart = useMemo(() => {
    const W = 1000, H = 320, padL = 50, padR = 20, padT = 20, padB = 40;
    const plotW = W - padL - padR, plotH = H - padT - padB;
    if (points.length === 0) return null;
    const tMin = points[0]!.date;
    const tMaxRaw = points[points.length - 1]!.date;
    const tSpan = Math.max(tMaxRaw - tMin, 1);
    const vMax = Math.max(...points.map((p) => p.value));
    const yMax = niceMax(vMax);
    const xs = points.map((p) => padL + ((p.date - tMin) / tSpan) * plotW);
    const ys = points.map((p) => padT + plotH - (p.value / yMax) * plotH);
    const yTicks = Array.from({ length: 6 }, (_, i) => {
      const y = padT + plotH - (i / 5) * plotH;
      const val = (i / 5) * yMax;
      return { y, label: yMax >= 10 ? Math.round(val).toString() : val.toFixed(1) };
    });
    // up to ~12 evenly spaced date ticks
    // chart-axis label — permitted §18.6 exception (M/YY tick, 24-hour-safe,
    // never AM/PM; not a data value)
    const nT = Math.min(12, Math.max(2, points.length));
    const xTicks = Array.from({ length: nT }, (_, i) => {
      const t = tMin + (i / (nT - 1)) * tSpan;
      const d = new Date(t);
      return { x: padL + (i / (nT - 1)) * plotW, label: `${d.getMonth() + 1}/${d.getFullYear() % 100}` };
    });
    const linePath = xs.map((x, i) => `${i ? 'L' : 'M'} ${x.toFixed(1)},${ys[i]!.toFixed(1)}`).join(' ');
    const areaPath = `M ${xs[0]!.toFixed(1)},${(padT + plotH)} ${xs
      .map((x, i) => `L ${x.toFixed(1)},${ys[i]!.toFixed(1)}`).join(' ')} L ${xs[xs.length - 1]!.toFixed(1)},${padT + plotH} Z`;
    return { W, H, padL, padR, padT, padB, plotH, xs, ys, yTicks, xTicks, linePath, areaPath };
  }, [points]);

  const showTip = (e: RMouse, p: Pt) => {
    const tip = tipRef.current; if (!tip) return;
    tip.innerHTML =
      `<div class="tr-tip-val">${p.raw} ${units}</div>` +
      `<div class="tr-tip-row">${p.dateStr}</div>` +
      `<div class="tr-tip-row" style="margin-top:3px">${p.site || '—'}</div>` +
      `<div class="tr-tip-row" style="opacity:.6">Job ${p.job}</div>`;
    tip.classList.add('on');
    moveTip(e);
  };
  const moveTip = (e: RMouse) => {
    const tip = tipRef.current; if (!tip || !tip.classList.contains('on')) return;
    tip.style.left = `${Math.min(e.clientX + 14, window.innerWidth - tip.offsetWidth - 10)}px`;
    tip.style.top = `${Math.max(e.clientY - 40, 4)}px`;
  };
  const hideTip = () => tipRef.current?.classList.remove('on');

  const runQuery = () => {
    // Site is optional here: in this ALPACA DB only ~47 of ~30k result
    // rows carry a site, so requiring it (like ALPACA OS) would return
    // almost nothing. Leave blank = all sites.
    setCommitted({ client, projectId, projectName, siteName, parameter, from: dateFrom, to: dateTo });
  };
  const resetQuery = () => {
    setClient([]); setProjectId([]); setProjectName([]); setSiteName([]); setParameter([]);
    setDateFrom(''); setDateTo(''); setChartType('line'); setCommitted(null);
  };

  // Grid rows after the page-level search box (AppDataGrid owns sort,
  // per-column filter and pagination).
  const gridRows = useMemo(() => {
    const q = gSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.jobId, r.sampleId, r.siteName, r.parameter, r.client, r.projectId, r.projectName]
        .some((v) => str(v).toLowerCase().includes(q)));
  }, [rows, gSearch]);

  // Shared-grid columns — Date uses the existing fmtDate; JobID + Result keep
  // their bold emphasis via a custom renderer (text mirrors it for filter/sort).
  const gridCols: AppColumn<Row>[] = [
    { field: 'dateAnalyzed', header: 'Date Analyzed', width: 110,
      render: r => fmtDate(r.dateAnalyzed), text: r => fmtDate(r.dateAnalyzed) },
    { field: 'jobId',       header: 'JobID',       width: 110,
      render: r => <strong>{str(r.jobId)}</strong>, text: r => str(r.jobId) },
    { field: 'sampleId',    header: 'SampleID',    width: 120, text: r => str(r.sampleId) },
    { field: 'siteName',    header: 'SiteName',    width: 170, text: r => str(r.siteName) },
    { field: 'parameter',   header: 'Parameter',   width: 150, text: r => str(r.parameter) },
    { field: 'result',      header: 'Result',      width: 90,
      render: r => <strong>{str(r.result)}</strong>, text: r => str(r.result) },
    { field: 'units',       header: 'Units',       width: 70,  text: r => str(r.units) },
    { field: 'rl',          header: 'RL',          width: 65,  text: r => str(r.rl) },
    { field: 'client',      header: 'Client',      width: 180, text: r => str(r.client) },
    { field: 'projectId',   header: 'ProjectID',   width: 130, text: r => str(r.projectId) },
    { field: 'projectName', header: 'ProjectName',              text: r => str(r.projectName) },
  ];

  const doExport = async (kind: 'pdf' | 'xlsx') => {
    setExpOpen(false);
    const base = `${(activeParam || 'trend').replace(/[^\w]+/g, '_')}_trend_${new Date().toISOString().slice(0, 10)}`;
    if (kind === 'xlsx') {
      const XLSX = await import('xlsx');
      const aoa = [
        ['Date Analyzed', 'JobID', 'SampleID', 'SiteName', 'Parameter', 'Result', 'Units', 'RL', 'Client', 'ProjectID', 'ProjectName'],
        ...gridRows.map((r) => [
          fmtDate(r.dateAnalyzed), str(r.jobId), str(r.sampleId), str(r.siteName), str(r.parameter),
          str(r.result), str(r.units), str(r.rl), str(r.client), str(r.projectId), str(r.projectName),
        ]),
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), 'Trend');
      XLSX.writeFile(wb, `${base}.xlsx`);
    } else {
      const svg = document.getElementById('tr-chart-svg') as SVGSVGElement | null;
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      doc.setFontSize(14);
      doc.text(`${activeParam || 'Trend'} — Trend Analysis (${points.length} results)`, 40, 40);
      if (svg) {
        const xml = new XMLSerializer().serializeToString(svg);
        const img = new Image();
        const url = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(xml)));
        await new Promise<void>((res) => { img.onload = () => res(); img.onerror = () => res(); img.src = url; });
        const canvas = document.createElement('canvas');
        canvas.width = 1000; canvas.height = 320;
        const ctx = canvas.getContext('2d');
        if (ctx) { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 1000, 320); ctx.drawImage(img, 0, 0, 1000, 320); }
        doc.addImage(canvas.toDataURL('image/png'), 'PNG', 40, 60, 760, 243);
      }
      doc.save(`${base}.pdf`);
    }
  };

  const c = chart;

  return (
    <div className="trend-page">
      <div className="tp-topbar">
        <div className="tp-breadcrumb">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>QA/QC</span><i className="ti ti-chevron-right" />Trend Analysis
        </div>
        {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour via
            navMeta('trendanalysis') + the SINGLE TOP load-time chip. */}
        <FormTitle pageId="trendanalysis" loadMs={loadMs} loading={committed != null && dataQ.isFetching} />
        <div className="tp-sub">
          Read-only · Time-series analytics across ALPACA LIMS
        </div>
      </div>

      <div className="tp-scroll">
        {/* Query Panel */}
        <div className="tp-card">
          <div className="tp-card-hdr">
            <div className="tp-card-title"><i className="ti ti-filter" /> Query Panel</div>
            {/* Shared toolbar — read-only analytics (no CRUD).  Run replaces
                Refresh and stays in the left slot alongside Reset; the chart
                export (PDF / XLSX) is the canonical "Export to", keeping its
                dropdown anchored via the right slot. */}
            <AppToolbar
              left={
                <>
                  <button className="ca-btn ca-btn-solid" type="button" onClick={runQuery}><i className="ti ti-search" /> Run Query</button>
                  <button className="ca-btn" type="button" onClick={resetQuery}><i className="ti ti-x" /> Reset</button>
                </>
              }
              right={
                <div className="export-menu-wrap" ref={expRef}>
                  <button className="ca-btn" type="button" onClick={() => setExpOpen((o) => !o)}>
                    <i className="ti ti-download" /> Export Chart <i className="ti ti-chevron-down" style={{ fontSize: 11, opacity: .6 }} />
                  </button>
                  {expOpen && (
                    <div className="export-menu">
                      <div className="export-menu-hdr">Export chart</div>
                      <div className="export-menu-item" onClick={() => doExport('pdf')}><i className="ti ti-file-type-pdf" /><span>PDF (.pdf)</span></div>
                      <div className="export-menu-item" onClick={() => doExport('xlsx')}><i className="ti ti-file-spreadsheet" /><span>Excel (.xlsx)</span></div>
                    </div>
                  )}
                </div>
              }
            />
          </div>
          <div className="tr-query-grid">
            <CheckedCombo label="Client" placeholder="(All)" column="client" selected={client} onChange={setClient} cascade={cascade} />
            <CheckedCombo label="Project Code" placeholder="(All)" column="projectId" selected={projectId} onChange={setProjectId} cascade={cascade} />
            <CheckedCombo label="Project Name" placeholder="(All)" column="projectName" selected={projectName} onChange={setProjectName} cascade={cascade} />
            <CheckedCombo label="Site Name" placeholder="(All)" column="siteName" selected={siteName} onChange={setSiteName} cascade={cascade} />
            <CheckedCombo label="Parameter" placeholder="(All)" column="parameter" selected={parameter} onChange={setParameter} cascade={cascade} />
          </div>
          <div className="tr-query-grid row2">
            <div className="tr-field">
              <div className="tr-field-lbl">Date Analyzed From</div>
              <input type="date" className="tr-field-sel" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="tr-field">
              <div className="tr-field-lbl">Date Analyzed To</div>
              <input type="date" className="tr-field-sel" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div className="tr-field">
              <div className="tr-field-lbl">Chart Type</div>
              <select className="tr-field-sel" value={chartType} onChange={(e) => setChartType(e.target.value as ChartType)}>
                <option value="line">Line</option>
                <option value="bar">Bar</option>
                <option value="area">Area</option>
              </select>
            </div>
            <div />
          </div>
        </div>

        {/* Chart */}
        <div className="tp-card">
          <div className="tp-card-hdr">
            <div className="tp-card-title">
              <i className="ti ti-chart-line" /> <span>{activeParam || '—'}</span> trend
              <span className="count">({points.length.toLocaleString()} results)</span>
            </div>
          </div>
          <div className="tr-chart-wrap">
            <div className="tr-chart-legend"><span className="swatch" /> {activeParam || '—'} {units && `(${units})`}</div>
            {committed == null ? (
              <div className="tr-chart-empty">Set filters and click <b>&nbsp;Run Query</b></div>
            ) : dataQ.isFetching ? (
              <div className="tr-chart-empty">Loading…</div>
            ) : !c ? (
              <div className="tr-chart-empty">No numeric data for the selected filters</div>
            ) : (
              <svg id="tr-chart-svg" className="tr-chart-svg" viewBox={`0 0 ${c.W} ${c.H}`} preserveAspectRatio="none">
                {c.yTicks.map((t, i) => (
                  <g key={`y${i}`}>
                    <line x1={c.padL} y1={t.y} x2={c.W - c.padR} y2={t.y} stroke="var(--ai-line)" strokeWidth={1} />
                    <text x={c.padL - 8} y={t.y + 3} textAnchor="end" fontSize={10} fill="var(--ai-muted)">{t.label}</text>
                  </g>
                ))}
                <text x={14} y={c.padT + c.plotH / 2} fontSize={10} fill="var(--ai-muted)"
                  transform={`rotate(-90 14 ${c.padT + c.plotH / 2})`} textAnchor="middle">{units}</text>
                <line x1={c.padL} y1={c.padT + c.plotH} x2={c.W - c.padR} y2={c.padT + c.plotH} stroke="var(--ai-line)" strokeWidth={1} />
                {c.xTicks.map((t, i) => (
                  <g key={`x${i}`}>
                    <line x1={t.x} y1={c.padT + c.plotH} x2={t.x} y2={c.padT + c.plotH + 4} stroke="var(--ai-line)" strokeWidth={1} />
                    <text x={t.x} y={c.padT + c.plotH + 16} textAnchor="middle" fontSize={10} fill="var(--ai-muted)">{t.label}</text>
                  </g>
                ))}
                {chartType === 'area' && <path d={c.areaPath} fill={CHART_COLOR} fillOpacity={0.22} stroke="none" />}
                {chartType !== 'bar' && <path d={c.linePath} fill="none" stroke={CHART_COLOR} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
                {chartType === 'bar' && points.map((p, i) => (
                  <rect key={`b${i}`} className="tr-chart-bar" x={c.xs[i]! - 3} y={c.ys[i]!} width={6}
                    height={(c.padT + c.plotH) - c.ys[i]!} fill={CHART_COLOR}
                    onMouseEnter={(e) => showTip(e, p)} onMouseMove={moveTip} onMouseLeave={hideTip} />
                ))}
                {points.map((p, i) => (
                  <circle key={`d${i}`} className="tr-chart-dot" cx={c.xs[i]} cy={c.ys[i]} r={4}
                    fill="#fff" stroke={CHART_COLOR} strokeWidth={2}
                    onMouseEnter={(e) => showTip(e, p)} onMouseMove={moveTip} onMouseLeave={hideTip} />
                ))}
              </svg>
            )}
          </div>
        </div>

        {/* Results search — shared .list-search-row ON the grey page (BO UI
            Standard v2), above its card, never inside it. */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input placeholder="Search job, sample, site, project…" value={gSearch}
              onChange={(e) => setGSearch(e.target.value)} />
          </div>
        </div>

        {/* Results grid — card holds the caption strip + grid only. */}
        <div className="tp-card">
          <div className="tp-card-hdr">
            {/* Card caption only — the ONE page heading (FormTitle + load-time
                chip) lives in the page header above (§18.6). */}
            <div className="tp-card-title">
              <i className="ti ti-table" /> Results
              <span className="count">({gridRows.length.toLocaleString()} rows)</span>
            </div>
          </div>
          {/* Shared data grid — styling, per-column filters, sort, footer /
              pagination and page-size are all standardised in <AppDataGrid>. */}
          <AppDataGrid<Row>
            rows={committed == null ? [] : gridRows}
            columns={gridCols}
            getRowId={(_r, i) => i}
            loading={committed != null && dataQ.isFetching}
            emptyText={committed == null ? 'No data — run a query above' : 'No results for the selected filters'}
          />
        </div>
      </div>
      <div className="tr-tip" ref={tipRef} />
    </div>
  );
}
